// pattern: functional-core
import { nanoid } from "nanoid";

export const LIGHT_LEVELS = ["shade", "partial", "full"] as const;
export type LightLevel = (typeof LIGHT_LEVELS)[number];

export interface Location {
	id: string;
	name: string;
	light: LightLevel;
	notes: string | null;
	created_at: number;
	updated_at: number;
}

export interface LocationInput {
	name: string;
	light: string;
	notes?: string;
}

interface ValidationResult {
	ok: true;
	value: LocationInput;
}

interface ValidationError {
	ok: false;
	error: string;
}

function str(input: Record<string, unknown>, key: string, fallback = ""): string {
	return String(input[key] ?? fallback);
}

function optStr(input: Record<string, unknown>, key: string): string | undefined {
	return input[key] ? String(input[key]).trim() : undefined;
}

export function validateLocationInput(
	input: Record<string, unknown>,
): ValidationResult | ValidationError {
	const name = str(input, "name").trim();
	if (!name) {
		return { ok: false, error: "name is required" };
	}

	const light = str(input, "light", "partial").trim();
	if (!LIGHT_LEVELS.includes(light as LightLevel)) {
		return { ok: false, error: `light must be one of: ${LIGHT_LEVELS.join(", ")}` };
	}

	const notes = optStr(input, "notes");

	return {
		ok: true,
		value: { name, light, notes },
	};
}

export function createLocation(input: LocationInput): Location {
	const now = Date.now();
	return {
		id: nanoid(12),
		name: input.name,
		light: input.light as LightLevel,
		notes: input.notes ?? null,
		created_at: now,
		updated_at: now,
	};
}
